/**
 * Hook: usePipelinesWithStages
 *
 * Todas as pipelines visíveis ao usuário no tenant atual, cada uma já com
 * suas etapas carregadas (ordenadas por `ordem`). Usado pelo seletor de
 * mover card entre pipelines/etapas.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePipelinesList } from './usePipelinesList';
import { Pipeline, PipelineStage } from '../types';

export interface PipelineWithStages {
  pipeline: Pipeline;
  stages: PipelineStage[];
}

export function usePipelinesWithStages() {
  const { pipelines, loading: loadingPipelines } = usePipelinesList();

  const pipelineIds = pipelines.map((p) => p.id);

  const { data: stagesByPipeline, isLoading: loadingStages } = useQuery({
    queryKey: ['pipelineStagesBatch', pipelineIds],
    queryFn: async () => await fetchStagesForPipelines(pipelineIds),
    enabled: pipelineIds.length > 0,
    staleTime: 0,
  });

  const pipelinesWithStages: PipelineWithStages[] = pipelines.map((pipeline) => ({
    pipeline,
    stages: stagesByPipeline?.get(pipeline.id) ?? [],
  }));

  return {
    pipelinesWithStages,
    loading: loadingPipelines || loadingStages,
  };
}

async function fetchStagesForPipelines(
  pipelineIds: string[]
): Promise<Map<string, PipelineStage[]>> {
  const { data, error } = await supabase
    .from('pipeline_stages')
    .select('*')
    .in('pipeline_id', pipelineIds)
    .order('ordem', { ascending: true });

  if (error) throw new Error(error.message);

  const grouped = new Map<string, PipelineStage[]>();
  for (const stage of data || []) {
    const typedStage = {
      ...stage,
      campos_obrigatorios: stage.campos_obrigatorios as string[] | undefined,
      visibilidade: stage.visibilidade as any,
    } as PipelineStage;
    const list = grouped.get(typedStage.pipeline_id) ?? [];
    list.push(typedStage);
    grouped.set(typedStage.pipeline_id, list);
  }
  return grouped;
}
